import { newBackgroundColors } from '@databases/color-picker/palettes';
import { type ObjectMode } from '@databases/types';
import {
  type Database,
  type DatabaseField,
  DatabaseFieldType,
  type SelectOption,
} from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import {
  mapColorToNewPalette,
  mapColorToBadgeToken,
  mapColorToIconTileAppearance,
} from '@databases/utils/database/field-types/select';
import { getDatabaseFieldOptional } from '@databases/utils/database/utils';
import {
  type ViewChangeType,
  ViewChangeTypes,
} from '@databases/utils/database/view/view';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { type DatabaseGroup } from './database-group/database-group';

const groupableFieldTypes = [DatabaseFieldType.SELECT, DatabaseFieldType.USER] as const;

export type GroupableFieldType = (typeof groupableFieldTypes)[number];

/**
 * Returns an array of the field types that are eligible for grouping.
 */
export function getGroupableFieldTypes(): DatabaseFieldType[] {
  if (
    getExperimentValue('databasesBoardGrouping', 'enabled', {
      fireExperimentExposure: false,
    })
  ) {
    return [...groupableFieldTypes];
  }

  // When grouping FF is disabled, we'll only allow grouping by select fields
  return [DatabaseFieldType.SELECT];
}

/**
 * Returns true if the given field type is eligible for grouping.
 */
export function isGroupableFieldType(type: DatabaseFieldType): boolean {
  return getGroupableFieldTypes().includes(type);
}

/**
 * Returns true if the given database field can be used as a group field.
 * Must be a single-select field whose type supports grouping.
 */
export function isValidGroupField(groupField: DatabaseField): boolean {
  return !groupField.settings.multi && getGroupableFieldTypes().includes(groupField.type);
}

/**
 * Returns the group field configured in the given layout settings. If no valid group field is configured, undefined is returned.
 */
export function getGroupField(
  database: Database<ObjectMode>,
  viewSettings: DatabaseViewSettings
): DatabaseField | undefined {
  const { grouping } = viewSettings;
  const groupFieldId = grouping?.groupBy?.fieldId;
  const groupField = getDatabaseFieldOptional(database, groupFieldId);
  return groupField && isValidGroupField(groupField) ? groupField : undefined;
}

/**
 * Returns the subgroup field configured in the layout settings. If no valid subgroup field is configured, undefined is returned.
 */
export function getSubgroupField(
  database: Database<ObjectMode>,
  viewSettings: DatabaseViewSettings
): DatabaseField | undefined {
  const { grouping } = viewSettings;

  const subgroupFieldId = grouping?.subgroupBy?.fieldId;
  const subgroupField = getDatabaseFieldOptional(database, subgroupFieldId);
  return subgroupField && isValidGroupField(subgroupField) ? subgroupField : undefined;
}

/**
 * Type guard function to check if a DatabaseGroup is for a SELECT field and has selectOption property.
 */
export function isSelectFieldGroup(
  group: DatabaseGroup
): group is DatabaseGroup & { selectOption?: SelectOption } {
  return group.groupField.type === DatabaseFieldType.SELECT;
}

export interface GroupStyling {
  backgroundColor: { backgroundColor: string };
  badgeColor: { backgroundColor: string };
  iconTileAppearance:
    | 'blue'
    | 'teal'
    | 'red'
    | 'green'
    | 'purple'
    | 'lime'
    | 'magenta'
    | 'orange'
    | 'yellow'
    | 'gray';
}

/**
 * Returns comprehensive styling information for a database group including background color,
 * badge color, and icon tile appearance based on the group's type and color.
 */
export function getGroupStyling(group: DatabaseGroup): GroupStyling {
  const isSelectField = isSelectFieldGroup(group);
  const optionColor = isSelectField ? group.selectOption?.color : undefined;

  return {
    backgroundColor: {
      backgroundColor:
        isSelectField && optionColor
          ? newBackgroundColors[mapColorToNewPalette(optionColor)] ||
            newBackgroundColors.greyLight
          : newBackgroundColors.greyLight,
    },
    badgeColor: {
      backgroundColor: isSelectField
        ? mapColorToBadgeToken(optionColor)
        : mapColorToBadgeToken(undefined),
    },
    iconTileAppearance: isSelectField
      ? mapColorToIconTileAppearance(optionColor)
      : 'gray',
  };
}

/**
 * Returns the first eligible field for automatic grouping, prioritizing SELECT fields first,
 * then other groupable field types.
 */
export function getFirstEligibleGroupField(
  database: Database<ObjectMode>
): DatabaseField | undefined {
  const fields = database.fields
    .map((fieldId) => getDatabaseFieldOptional(database, fieldId))
    .filter((field): field is DatabaseField => field !== undefined);

  const selectField = fields.find(
    (field) => field.type === DatabaseFieldType.SELECT && isValidGroupField(field)
  );

  if (selectField) {
    return selectField;
  }

  return fields.find(
    (field) => field.type !== DatabaseFieldType.SELECT && isValidGroupField(field)
  );
}

/**
 * Returns the default manual order of groups when grouping by a given field.
 *
 * For SELECT fields, this is based on the order of select options.
 * Other field types have no specific order, since they default to ascending sort.
 */
function getDefaultGroupOrder(groupField: DatabaseField): string[] | undefined {
  if (groupField.type === DatabaseFieldType.SELECT && groupField.settings.selectOptions) {
    return groupField.settings.selectOptions.map((option) => option.id ?? option.label);
  }
}

/** Sets the group (or subgroup) field for the view, setting a default manual group order if applicable. */
export function setGroupFieldForView(
  setViewChange: (delta: ViewChangeType | ViewChangeType[]) => void,
  groupField: DatabaseField,
  isSubgroup = false
) {
  setViewChange({
    type: ViewChangeTypes.VIEW_GROUPING_FIELD,
    fieldId: groupField?.id,
    isSubgroup,
    manualGroupOrder: getDefaultGroupOrder(groupField),
  });
}
